
import React, { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../../firebase';
import { Settings, Save, CheckCircle2, CreditCard, ShieldCheck, Loader2, X, Truck } from 'lucide-react';

const AdminSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'payment' | 'shipping'>('payment');

  // Ödeme Yapılandırması (Iyzico)
  const [paymentConfig, setPaymentConfig] = useState({
    apiKey: '',
    secretKey: '',
    active: false,
    sandbox: true
  });

  // Kargo Yapılandırması
  const [shippingConfig, setShippingConfig] = useState({
    threshold: 250,
    fee: 45
  });

  useEffect(() => {
    onValue(ref(db, 'settings/payment/iyzico'), (snapshot) => {
      if (snapshot.exists()) setPaymentConfig(prev => ({ ...prev, ...snapshot.val() }));
    });
    onValue(ref(db, 'settings/shipping'), (snapshot) => {
      if (snapshot.exists()) setShippingConfig(prev => ({ ...prev, ...snapshot.val() }));
    });
  }, []);

  const handleSavePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await set(ref(db, 'settings/payment/iyzico'), paymentConfig);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert("Ödeme ayarları kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveShipping = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await set(ref(db, 'settings/shipping'), shippingConfig);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert("Kargo ayarları kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-8 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-black text-stone-900 flex items-center">
            <Settings className="mr-3 text-amber-600" size={32} /> Genel Ayarlar
          </h1>
          <p className="text-stone-500 font-medium italic mt-1">Sistem altyapısı ve entegrasyon yönetimi.</p>
        </div>
        
        {success && (
          <div className="flex items-center space-x-2 bg-emerald-50 text-emerald-600 px-6 py-3 rounded-2xl border border-emerald-100 animate-bounce">
            <CheckCircle2 size={18} />
            <span className="text-sm font-bold">Ayarlar Güncellendi</span>
          </div>
        )}
      </div>

      <div className="flex bg-white p-1.5 rounded-2xl border border-stone-100 shadow-sm mb-8 w-fit">
        <button 
          onClick={() => setActiveTab('payment')}
          className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center space-x-2 ${activeTab === 'payment' ? 'bg-stone-900 text-white shadow-lg' : 'text-stone-400 hover:text-stone-900'}`}
        >
          <CreditCard size={16} />
          <span>Ödeme Altyapısı</span>
        </button>
        <button 
          onClick={() => setActiveTab('shipping')}
          className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center space-x-2 ${activeTab === 'shipping' ? 'bg-stone-900 text-white shadow-lg' : 'text-stone-400 hover:text-stone-900'}`}
        >
          <Truck size={16} />
          <span>Kargo Ayarları</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
          {activeTab === 'payment' ? (
            <form onSubmit={handleSavePayment} className="space-y-8 bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                    <CreditCard size={20} />
                  </div>
                  <h3 className="text-xl font-black text-stone-900">Iyzico Konfigürasyonu</h3>
                </div>
                <div className="flex items-center space-x-3">
                   <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${paymentConfig.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {paymentConfig.active ? 'AKTİF' : 'PASİF'}
                   </span>
                   <button 
                    type="button"
                    onClick={() => setPaymentConfig({...paymentConfig, active: !paymentConfig.active})}
                    className={`p-1.5 rounded-lg transition-all ${paymentConfig.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}
                   >
                      {paymentConfig.active ? <ShieldCheck size={24} /> : <X size={24} />}
                   </button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">API Key</label>
                    <input 
                      type="text"
                      value={paymentConfig.apiKey}
                      onChange={e => setPaymentConfig({...paymentConfig, apiKey: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                    />
                 </div>
                 <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Secret Key</label>
                    <input 
                      type="password"
                      value={paymentConfig.secretKey}
                      onChange={e => setPaymentConfig({...paymentConfig, secretKey: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                    />
                 </div>
                 <div className="col-span-2">
                    <label className="flex items-center space-x-3 cursor-pointer group p-6 bg-stone-50 rounded-[2rem] border border-stone-100">
                       <input 
                        type="checkbox"
                        checked={paymentConfig.sandbox}
                        onChange={e => setPaymentConfig({...paymentConfig, sandbox: e.target.checked})}
                        className="w-6 h-6 rounded-lg border-stone-300 text-amber-800 focus:ring-amber-500"
                       />
                       <div>
                          <p className="font-bold text-stone-900">Sandbox (Test) Modu</p>
                          <p className="text-xs text-stone-400 font-medium italic">Gerçek ödeme almaz, test kartlarıyla işlem yapılır.</p>
                       </div>
                    </label>
                 </div>
              </div>

              <div className="pt-6 border-t border-stone-50">
                 <button 
                  type="submit" disabled={loading}
                  className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-amber-800 transition-all shadow-xl disabled:opacity-50"
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                  <span>Ödeme Ayarlarını Kaydet</span>
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSaveShipping} className="space-y-8 bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center">
                  <Truck size={20} />
                </div>
                <h3 className="text-xl font-black text-stone-900">Kargo Ücretlendirme Ayarları</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Kargo Ücretsiz Alt Sınırı (TL)</label>
                    <input 
                      type="number"
                      value={shippingConfig.threshold}
                      onChange={e => setShippingConfig({...shippingConfig, threshold: Number(e.target.value)})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                      placeholder="Örn: 250"
                    />
                    <p className="text-[9px] text-stone-400 mt-2 font-medium px-2 italic">Bu tutarın üzerindeki siparişlerde kargo ücretsiz olur.</p>
                 </div>
                 <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Sabit Kargo Ücreti (TL)</label>
                    <input 
                      type="number"
                      value={shippingConfig.fee}
                      onChange={e => setShippingConfig({...shippingConfig, fee: Number(e.target.value)})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                      placeholder="Örn: 45"
                    />
                    <p className="text-[9px] text-stone-400 mt-2 font-medium px-2 italic">Alt sınırın altındaki siparişlere eklenecek ücret.</p>
                 </div>
              </div>

              <div className="pt-6 border-t border-stone-50">
                 <button 
                  type="submit" disabled={loading}
                  className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-amber-800 transition-all shadow-xl disabled:opacity-50"
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                  <span>Kargo Ayarlarını Kaydet</span>
                </button>
              </div>
            </form>
          )}
      </div>
    </div>
  );
};

export default AdminSettings;
